"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { useSeizeSettings } from "@/contexts/SeizeSettingsContext";
import { useAuth } from "@/components/auth/Auth";
import MyStreamActionTooltip from "@/components/brain/my-stream/MyStreamActionTooltip";
import { usePinnedWavesServer, MAX_PINNED_WAVES } from "@/hooks/usePinnedWavesServer";
interface WaveHeaderPinButtonProps {
  readonly waveId: string;
}

// Constants to reduce string duplication
const PIN_ACTIONS = {
  PIN: "Pin wave",
  UNPIN: "Unpin wave"
} as const;
const WaveHeaderPinButton: React.FC<WaveHeaderPinButtonProps> = t0 => {
  const $ = _c(47);
  const {
    waveId
  } = t0;
  const {
    waves
  } = useMyStream();
  const {
    isAnnouncementsWave,
    isLoaded: isSettingsLoaded
  } = useSeizeSettings();
  const {
    pinnedIds,
    isOperationInProgress,
    canPinWave
  } = usePinnedWavesServer();
  const {
    setToast,
    connectedProfile,
    activeProfileProxy
  } = useAuth();
  const [showMaxLimitTooltip, setShowMaxLimitTooltip] = useState(false);
  let t1;
  if ($[0] !== isOperationInProgress || $[1] !== waveId) {
    t1 = isOperationInProgress(waveId);
    $[0] = isOperationInProgress;
    $[1] = waveId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isCurrentlyProcessing = t1;
  let t2;
  if ($[3] !== pinnedIds || $[4] !== waveId) {
    t2 = pinnedIds.includes(waveId);
    $[3] = pinnedIds;
    $[4] = waveId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isPinned = t2;
  const isOfficialWave = waves.list.some(wave => wave.id === waveId && wave.isOfficial);
  let t3;
  if ($[6] !== canPinWave || $[7] !== waveId) {
    t3 = canPinWave(waveId);
    $[6] = canPinWave;
    $[7] = waveId;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const canPinCurrentWave = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setShowMaxLimitTooltip(false);
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const hideTooltip = t4;
  let t5;
  if ($[10] !== setToast) {
    t5 = message => {
      setToast({
        type: "error",
        message
      });
    };
    $[10] = setToast;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const showErrorToast = t5;
  let t6;
  bb0: {
    if (isPinned) {
      t6 = PIN_ACTIONS.UNPIN;
      break bb0;
    }
    if (canPinCurrentWave) {
      t6 = PIN_ACTIONS.PIN;
      break bb0;
    }
    t6 = `Max ${MAX_PINNED_WAVES} pinned waves. Unpin another wave first.`;
  }
  const tooltipContent = t6;
  let t7;
  bb1: {
    if (isPinned) {
      t7 = "tw-border-0 tw-bg-iron-800 tw-text-iron-100 tw-ring-1 tw-ring-inset tw-ring-white/10 desktop-hover:hover:tw-bg-iron-700 desktop-hover:hover:tw-text-white";
      break bb1;
    }
    t7 = "tw-border-0 tw-text-iron-500 desktop-hover:hover:tw-text-iron-300 desktop-hover:hover:tw-bg-iron-700 tw-bg-transparent active:tw-bg-iron-700";
  }
  const buttonStyles = t7;
  const ariaLabel = isPinned ? PIN_ACTIONS.UNPIN : PIN_ACTIONS.PIN;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      hideTooltip();
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== isPinned) {
    t9 = [isPinned, hideTooltip];
    $[13] = isPinned;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  if ($[15] !== canPinCurrentWave) {
    t10 = () => {
      if (canPinCurrentWave) {
        hideTooltip();
      }
    };
    $[15] = canPinCurrentWave;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== canPinCurrentWave || $[18] !== pinnedIds) {
    t11 = [pinnedIds, canPinCurrentWave, hideTooltip];
    $[17] = canPinCurrentWave;
    $[18] = pinnedIds;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[20] !== showMaxLimitTooltip) {
    t12 = () => {
      if (!showMaxLimitTooltip) {
        return;
      }
      const timer = setTimeout(hideTooltip, 3000);
      return () => clearTimeout(timer);
    };
    t13 = [showMaxLimitTooltip, hideTooltip];
    $[20] = showMaxLimitTooltip;
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  useEffect(t12, t13);
  if (!connectedProfile?.handle || activeProfileProxy) {
    return null;
  }
  if (isOfficialWave) {
    return null;
  }
  if (!isPinned && !isSettingsLoaded) {
    return null;
  }
  if (isAnnouncementsWave(waveId) && !isPinned) {
    return null;
  }
  let t14;
  if ($[23] !== canPinWave || $[24] !== isCurrentlyProcessing || $[25] !== isPinned || $[26] !== showErrorToast || $[27] !== waveId || $[28] !== waves) {
    t14 = async e => {
      e.stopPropagation();
      e.preventDefault();
      if (isCurrentlyProcessing) {
        return;
      }
      ;
      try {
        if (isPinned) {
          waves.removePinnedWave(waveId);
          hideTooltip();
          return;
        }
        if (!canPinWave(waveId)) {
          setShowMaxLimitTooltip(true);
          showErrorToast(`Maximum ${MAX_PINNED_WAVES} pinned waves allowed`);
          return;
        }
        waves.addPinnedWave(waveId);
      } catch (t15) {
        const error = t15;
        console.error("Error updating wave pin status:", error);
        const errorMessage = error instanceof Error ? error.message : "Something went wrong";
        const action = isPinned ? "unpin" : "pin";
        showErrorToast(`Failed to ${action} wave: ${errorMessage}`);
      }
    };
    $[23] = canPinWave;
    $[24] = isCurrentlyProcessing;
    $[25] = isPinned;
    $[26] = showErrorToast;
    $[27] = waveId;
    $[28] = waves;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const handleClick = t14;
  const t15 = `tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-lg tw-transition-all tw-duration-200 ${buttonStyles} ${isCurrentlyProcessing ? "tw-cursor-not-allowed tw-opacity-50" : ""}`;
  const t16 = `wave-header-pin-${waveId}`;
  const t17 = `tw-size-4 tw-flex-shrink-0 ${isPinned ? "tw-rotate-[-45deg]" : ""}`;
  let t18;
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <path d="M12 17v5" />;
    t19 = <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />;
    $[30] = t18;
    $[31] = t19;
  } else {
    t18 = $[30];
    t19 = $[31];
  }
  let t20;
  if ($[32] !== t17) {
    t20 = <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={t17} aria-hidden="true">{t18}{t19}</svg>;
    $[32] = t17;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== ariaLabel || $[35] !== handleClick || $[36] !== isCurrentlyProcessing || $[37] !== t15 || $[38] !== t16 || $[39] !== t20 || $[40] !== tooltipContent) {
    t21 = <button onClick={handleClick} disabled={isCurrentlyProcessing} className={t15} aria-label={ariaLabel} data-tooltip-id={t16} data-tooltip-content={tooltipContent}>{t20}</button>;
    $[34] = ariaLabel;
    $[35] = handleClick;
    $[36] = isCurrentlyProcessing;
    $[37] = t15;
    $[38] = t16;
    $[39] = t20;
    $[40] = tooltipContent;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  const t22 = `wave-header-pin-${waveId}`;
  let t23;
  if ($[42] !== t22) {
    t23 = <MyStreamActionTooltip id={t22} />;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t21 || $[45] !== t23) {
    t24 = <>{t21}{t23}</>;
    $[44] = t21;
    $[45] = t23;
    $[46] = t24;
  } else {
    t24 = $[46];
  }
  return t24;
};
export default WaveHeaderPinButton;